import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Banknote, Coffee, CreditCard, X } from "lucide-react";
import { pos } from "../../services/posService";

export const BRAND = "#34251D";

export function POSHeader({ title, subtitle, icon: Icon = Coffee, back = "/pos", right }) {
	return (
		<header className="flex flex-wrap items-center justify-between gap-3 bg-[#34251D] px-4 py-4 text-white sm:px-6">
			<div className="flex items-center gap-3">
				<Icon size={26} aria-hidden="true" />
				<div>
					<h1 className="text-xl font-bold">{title}</h1>
					{subtitle ? <p className="text-xs text-white/70">{subtitle}</p> : null}
				</div>
			</div>
			<div className="flex items-center gap-2">
				{right}
				{back ? (
					<Link
						to={back}
						className="flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm transition hover:bg-white/20"
					>
						<ArrowLeft size={17} aria-hidden="true" /> Tableau de bord
					</Link>
				) : null}
			</div>
		</header>
	);
}

export function MethodBadge({ method }) {
	if (method === "cash") {
		return (
			<span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
				<Banknote size={13} aria-hidden="true" /> Espèces
			</span>
		);
	}
	if (method === "terminal") {
		return (
			<span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-800">
				<CreditCard size={13} aria-hidden="true" /> Terminal
			</span>
		);
	}
	return <span className="text-xs text-gray-500">—</span>;
}

const STATUS = {
	paid: ["Payée", "bg-emerald-50 text-emerald-800"],
	awaiting_payment: ["En attente", "bg-amber-50 text-amber-800"],
	void: ["Annulée", "bg-gray-100 text-gray-600"],
	refunded: ["Remboursée", "bg-red-50 text-red-700"],
	partially_refunded: ["Remb. partiel", "bg-red-50 text-red-700"],
	draft: ["Brouillon", "bg-gray-100 text-gray-600"],
};

export function SaleStatusBadge({ status }) {
	const [label, cls] = STATUS[status] || [status, "bg-gray-100 text-gray-600"];
	return <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${cls}`}>{label}</span>;
}

export function Modal({ title, onClose, children, wide }) {
	useEffect(() => {
		if (!onClose) return undefined;
		const onKey = (event) => event.key === "Escape" && onClose();
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [onClose]);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
			onMouseDown={(event) => {
				if (onClose && event.target === event.currentTarget) onClose();
			}}
		>
			<div
				role="dialog"
				aria-modal="true"
				className={`max-h-[92vh] w-full overflow-y-auto rounded-2xl bg-white shadow-2xl ${wide ? "max-w-2xl" : "max-w-lg"}`}
			>
				<div className="flex items-start justify-between gap-3 border-b p-5">
					<h2 className="text-xl font-bold text-[#34251D]">{title}</h2>
					{onClose ? (
						<button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-2 hover:bg-[#F7F3ED]">
							<X size={20} />
						</button>
					) : null}
				</div>
				<div className="p-5">{children}</div>
			</div>
		</div>
	);
}

export function ErrorBox({ message }) {
	if (!message) return null;
	return (
		<div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
			{message}
		</div>
	);
}

/** Session de caisse ouverte (ou null) + totaux, rechargeable. */
export function useCurrentSession() {
	const [state, setState] = useState({ loading: true, error: "", data: null });

	const reload = useCallback(async () => {
		setState((s) => ({ ...s, loading: true, error: "" }));
		try {
			const data = await pos.currentSession();
			setState({ loading: false, error: "", data });
		} catch (e) {
			setState({ loading: false, error: e.message, data: null });
		}
	}, []);

	useEffect(() => {
		reload();
	}, [reload]);

	return {
		loading: state.loading,
		error: state.error,
		register: state.data?.register || null,
		session: state.data?.session || null,
		testMode: !!state.data?.test_mode,
		vatRates: state.data?.vat_rates || {},
		reload,
	};
}

export const timeOf = (iso) =>
	iso ? new Date(iso).toLocaleTimeString("fr-CH", { hour: "2-digit", minute: "2-digit" }) : "—";

export const dateTimeOf = (iso) =>
	iso
		? new Date(iso).toLocaleString("fr-CH", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "—";
